import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  Typography,
  Box,
  Divider,
  Skeleton,
} from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import StraightenIcon from "@mui/icons-material/Straighten";
import { useTranslation } from "react-i18next";

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    backgroundColor: "#183059",
    color: theme.palette.common.white,
    fontSize: 16,
    fontWeight: 600,
    padding: "12px 20px",
    "&:first-of-type": {
      borderTopLeftRadius: "8px",
    },
    "&:last-child": {
      borderTopRightRadius: "8px",
    },
  },
  "&.MuiTableCell-body": {
    fontSize: 14,
    color: "#333",
    padding: "16px 20px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "rgba(24, 48, 89, 0.04)",
    transition: "background-color 0.2s ease",
  },
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  marginBottom: "16px",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  overflow: "hidden",
}));

const CellContent = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const DataCell = styled(Typography)(({ isStale }) => ({
  opacity: isStale ? 0.7 : 1,
  transition: "opacity 0.3s ease",
}));

const SummaryValueSkeleton = ({ compact = false }) => (
  <Skeleton
    variant="rounded"
    width={compact ? 96 : 150}
    height={compact ? 34 : 28}
    sx={{
      bgcolor: "#E7EEF7",
      borderRadius: compact ? "4px" : "8px",
    }}
  />
);

// Constants
const CONSTANTS = {
  STOP_RADIUS: 50, // meters - radius to detect if vehicle is at a stop
  STOP_VISIT_TIME: 10, // seconds - time needed at a stop to mark it as visited
  DEPARTURE_DISTANCE: 50, // meters - distance from last stop to consider "departed"
  LOOK_AHEAD_DISTANCE: 2000, // meters - maximum distance to look ahead for future stops
  DATA_STALENESS_THRESHOLD: 30000,
  EARTH_RADIUS: 6371e3,
  MAX_STOP_DISTANCE: 4000, // Maximum reasonable distance between stops (4km)
  MIN_STOP_DISTANCE: 200, // Minimum distance between stops (200m)
  AVG_SPEED: 25, // Average speed in km/h
  MIN_STOP_TIME: 60, // Minimum time between stops in seconds (1 min)
  MAX_STOP_TIME: 900, // Maximum time between stops in seconds (15 min)
  TRAFFIC_MULTIPLIER: 1.1, // 10% buffer for traffic
};

const calculateRealisticETA = (distance) => {
  // Convert distance to km for speed calculation
  const distanceInKm = distance / 1000;

  // Calculate base time in seconds
  let baseTime = (distanceInKm / CONSTANTS.AVG_SPEED) * 3600;

  // Add small traffic buffer
  baseTime *= CONSTANTS.TRAFFIC_MULTIPLIER;

  // Add realistic stop light delays
  const numTrafficLights = Math.floor(distance / 800); // One light every 800m
  const avgTrafficLightDelay = 20; // 20 seconds per light
  baseTime += numTrafficLights * avgTrafficLightDelay;

  // Constrain to realistic minimum and maximum times
  return Math.min(
    Math.max(Math.round(baseTime), CONSTANTS.MIN_STOP_TIME),
    CONSTANTS.MAX_STOP_TIME
  );
};

const calculateImprovedDistance = (pos1, pos2) => {
  if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
    return Infinity;

  try {
    const φ1 = (pos1.lat * Math.PI) / 180;
    const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
    const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
    const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = CONSTANTS.EARTH_RADIUS * c;

    // Constrain to realistic values
    return Math.min(
      Math.max(distance, CONSTANTS.MIN_STOP_DISTANCE),
      CONSTANTS.MAX_STOP_DISTANCE
    );
  } catch (error) {
    console.error("Error calculating distance:", error);
    return Infinity;
  }
};

const formatTime = (timeString) => {
  if (!timeString) return "---";

  // If it's already in the format we want, return it
  if (
    typeof timeString === "string" &&
    (timeString.includes("m") || timeString.includes("s"))
  ) {
    return timeString;
  }

  // Convert to number if it's a string
  const seconds =
    typeof timeString === "string" ? parseInt(timeString) : timeString;
  if (isNaN(seconds) || seconds <= 0) return "---";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const NextStopTableScreen = ({
  details,
  stops = [],
  stopDetails = {},
  isInbound,
  currentPosition,
  routeCoordinates = [],
  loading = false,
}) => {
  const { t } = useTranslation()
  const [visitedStops, setVisitedStops] = useState(new Set());
  const [stopVisitTimer, setStopVisitTimer] = useState(null);
  const [currentStopId, setCurrentStopId] = useState(null);
  const [previousData, setPreviousData] = useState({
    nextStop: null,
    stopDetail: null,
    lastUpdateTime: null,
  });
  const [lastStopDistance, setLastStopDistance] = useState(null);
  const [lastVisitedStop, setLastVisitedStop] = useState(null);

  const getBestMetrics = (
    calculatedDistance,
    backendDistance,
    calculatedETA,
    backendETA
  ) => {
    let finalDistance = calculatedDistance;
    let finalETA = calculatedETA;

    // Convert backend distance to meters if it's in km
    if (backendDistance) {
      const backendInMeters =
        backendDistance > 100 ? backendDistance : backendDistance * 1000;

      // Take the smaller value if both exist and the difference isn't too large
      if (calculatedDistance && backendInMeters) {
        const diff = Math.abs(calculatedDistance - backendInMeters);
        if (diff / calculatedDistance < 0.5) {
          // If difference is less than 50%
          finalDistance = Math.min(calculatedDistance, backendInMeters);
        } else {
          finalDistance = calculatedDistance; // Use calculated if difference is too large
        }
      }
    }

    // Compare ETAs
    if (backendETA && calculatedETA) {
      const diff = Math.abs(calculatedETA - backendETA);
      if (diff / calculatedETA < 0.5) {
        // If difference is less than 50%
        finalETA = Math.min(calculatedETA, backendETA);
      } else {
        finalETA = calculatedETA; // Use calculated if difference is too large
      }
    }

    return {
      distance: finalDistance / 1000, // Convert to km for display
      eta: finalETA,
    };
  };

  // Calculate distance between two points
  const calculateDistance = (pos1, pos2) => {
    if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
      return Infinity;

    try {
      const φ1 = (pos1.lat * Math.PI) / 180;
      const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
      const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
      const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return CONSTANTS.EARTH_RADIUS * c;
    } catch (error) {
      console.error("Error calculating distance:", error);
      return Infinity;
    }
  };

  // Find route index for position
  const findRouteIndex = (position) => {
    if (!position || !routeCoordinates?.length) return -1;

    let minDistance = Infinity;
    let closestIndex = -1;

    routeCoordinates.forEach((coord, index) => {
      const distance = calculateDistance(position, {
        latitude: coord.latitude,
        longitude: coord.longitude,
      });

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  // Reset states when direction changes
  useEffect(() => {
    setVisitedStops(new Set());
    setStopVisitTimer(null);
    setCurrentStopId(null);
    setPreviousData({
      nextStop: null,
      stopDetail: null,
      lastUpdateTime: null,
    });
  }, [isInbound]);

  // Determine next stop and match with current details
  const { nextStop, stopDetail } = useMemo(() => {
    try {
      if (!currentPosition || !stops?.length || !routeCoordinates?.length) {
        return previousData;
      }

      const currentIndex = findRouteIndex(currentPosition);
      if (currentIndex === -1) return previousData;

      // Check distance from last visited stop
      let distanceFromLastStop = Infinity;
      if (lastVisitedStop) {
        distanceFromLastStop = calculateImprovedDistance(
          currentPosition,
          lastVisitedStop
        );
        setLastStopDistance(distanceFromLastStop);
      }

      if (distanceFromLastStop < CONSTANTS.DEPARTURE_DISTANCE) {
        return previousData;
      }

      // Find all unvisited stops with improved distance calculation
      const stopsWithData = stops
        .filter((stop) => !visitedStops.has(stop.stopId))
        .map((stop) => ({
          ...stop,
          routeIndex: findRouteIndex({
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          }),
          distance: calculateImprovedDistance(currentPosition, stop),
        }))
        .filter((stop) => stop.routeIndex !== -1);

      const validStops = stopsWithData
        .filter((stop) =>
          isInbound
            ? stop.routeIndex <= currentIndex
            : stop.routeIndex >= currentIndex
        )
        .sort((a, b) =>
          isInbound ? b.routeIndex - a.routeIndex : a.routeIndex - b.routeIndex
        );

      if (!validStops.length) return previousData;

      const nextValidStop = validStops[0];
      const backendStopDetail = stopDetails[nextValidStop.stopId];

      // Calculate frontend metrics
      const calculatedDistance = nextValidStop.distance;
      const calculatedETA = calculateRealisticETA(calculatedDistance);

      // Get best metrics by comparing backend and frontend calculations
      const bestMetrics = getBestMetrics(
        calculatedDistance,
        backendStopDetail?.distance,
        calculatedETA,
        backendStopDetail?.eta
      );

      // Return combined metrics
      return {
        nextStop: nextValidStop,
        stopDetail: {
          ...backendStopDetail,
          distance: bestMetrics.distance,
          eta: bestMetrics.eta,
        },
      };
    } catch (error) {
      console.error("Error determining next stop:", error);
      return previousData;
    }
  }, [
    currentPosition,
    stops,
    stopDetails,
    routeCoordinates,
    isInbound,
    visitedStops,
    lastVisitedStop,
    lastStopDistance,
  ]);

  // Update previous data when we get valid new data
  useEffect(() => {
    if (nextStop && stopDetail) {
      setPreviousData({
        nextStop,
        stopDetail,
        lastUpdateTime: Date.now(),
      });
    }
  }, [nextStop, stopDetail]);

  // Track stop visits
  useEffect(() => {
    if (!currentPosition || !stops?.length) return;

    stops.forEach((stop) => {
      const distance = calculateDistance(currentPosition, stop);
      if (distance <= CONSTANTS.STOP_RADIUS) {
        if (!stopVisitTimer && !visitedStops.has(stop.stopId)) {
          setStopVisitTimer({
            stopId: stop.stopId,
            startTime: Date.now(),
          });
        } else if (stopVisitTimer?.stopId === stop.stopId) {
          const timeSpent = (Date.now() - stopVisitTimer.startTime) / 1000;
          if (timeSpent >= CONSTANTS.STOP_VISIT_TIME) {
            setVisitedStops((prev) => new Set([...prev, stop.stopId]));
            setStopVisitTimer(null);
          }
        }
      } else if (stopVisitTimer?.stopId === stop.stopId) {
        setStopVisitTimer(null);
      }
    });
  }, [currentPosition, stops, visitedStops, stopVisitTimer]);

  // Determine if data is stale
  const isDataStale = useMemo(() => {
    if (!nextStop || !stopDetail) return true;
    if (!previousData.lastUpdateTime) return false;
    return (
      Date.now() - previousData.lastUpdateTime >
      CONSTANTS.DATA_STALENESS_THRESHOLD
    );
  }, [nextStop, stopDetail, previousData.lastUpdateTime]);

  return (

    <Box
      sx={{
        borderRadius: "10px",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" }, // Stack vertically on small screens, horizontally on larger screens
        justifyContent: "space-between",
        padding: { xs: "15px", sm: "15px 30px", md: "20px 60px" }, // Adjust padding for different screen sizes
        backgroundColor: "#FFFFFF",
        border: "1px solid #A5DCF3",
        boxShadow: "-2px 1px 5px 1px #00000040",
        gap: { xs: 2, sm: 0 } // Add gap between items on small screens
      }}
    >
      {/* Next Stop Section */}
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        mb: { xs: 2, sm: 0 } // Add bottom margin on small screens
      }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "16px", md: "20px" }, // Smaller font on small screens
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
              color: "#183059"
            }}
          >
            {t("Next Stop")}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Roboto",
            fontWeight: 500,
            fontSize: { xs: "20px", md: "24px" }, // Smaller font on small screens
            lineHeight: "100%",
            letterSpacing: "0%",
            textAlign: "center",
            color: isDataStale ? "text.disabled" : "#111214"
          }}
        >
          {loading || !nextStop ? <SummaryValueSkeleton /> : nextStop.name}
        </Typography>
      </Box>

      {/* Vertical Divider - Hide on small screens */}
      <Divider
        orientation={{ xs: "horizontal", sm: "vertical" }} // Horizontal on small screens, vertical on larger screens
        flexItem
        sx={{
          mx: { sm: 2 },
          my: { xs: 2, sm: 0 }, // Add vertical margin on small screens
          height: { xs: "1px", sm: "60px" }, // 1px height for horizontal divider
          width: { xs: "100%", sm: "auto" }, // Full width on small screens
          alignSelf: "center",
          border: "1px solid #183059",
          display: { xs: "flex", sm: "flex" } // Show on all screen sizes
        }}
      />

      {/* Distance Section */}
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        mb: { xs: 2, sm: 0 } // Add bottom margin on small screens
      }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "16px", md: "20px" }, // Smaller font on small screens
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
              color: "#183059"
            }}
          >
            {t("Distance Left")}
          </Typography>
        </Box>
        {loading || !nextStop ? (
          <SummaryValueSkeleton />
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "20px", md: "24px" }, // Smaller font on small screens
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
              color: "#20A8E0" // primary color
            }}
          >
            {`${(calculateImprovedDistance(currentPosition, nextStop) / 1000).toFixed(1)} km`}
          </Typography>
        )}
      </Box>

      {/* Vertical Divider - Hide on small screens */}
      <Divider
        orientation={{ xs: "horizontal", sm: "vertical" }} // Horizontal on small screens, vertical on larger screens
        flexItem
        sx={{
          mx: { sm: 2 },
          my: { xs: 2, sm: 0 }, // Add vertical margin on small screens
          height: { xs: "1px", sm: "60px" }, // 1px height for horizontal divider
          width: { xs: "100%", sm: "auto" }, // Full width on small screens
          alignSelf: "center",
          border: "1px solid #183059",
          display: { xs: "flex", sm: "flex" } // Show on all screen sizes
        }}
      />

      {/* ETA Section */}
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1
      }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "16px", md: "20px" }, // Smaller font on small screens
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
              color: "#183059"
            }}
          >
            {t("ETA")}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: "rgba(24, 48, 89, 0.08)",
            padding: { xs: "3px 12px", sm: "4px 16px" }, // Adjust padding for different screen sizes
            borderRadius: "4px",
            display: "inline-block",
            textAlign: "center",
          }}
        >
          {loading || !nextStop ? (
            <SummaryValueSkeleton compact />
          ) : (
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "20px", md: "24px" }, // Smaller font on small screens
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#20A8E0" // primary color
              }}
            >
              {formatTime(
                calculateRealisticETA(
                  calculateImprovedDistance(currentPosition, nextStop)
                )
              )}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NextStopTableScreen;
